import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Building2, Sparkles } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);

  if (user) return <Navigate to="/dashboard" replace />;

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (k === "email") {
      const v = e.target.value.trim().toLowerCase();
      const at = v.indexOf("@");
      if (at > 0 && v.length > at + 1 && v.includes(".", at)) {
        const domain = v.split("@")[1];
        setHint({ domain });
      } else {
        setHint(null);
      }
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to register");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-md bg-zinc-950 text-white flex items-center justify-center font-semibold">L</div>
          <span className="font-display text-lg font-semibold tracking-tight">Ledgerly</span>
        </div>

        <div className="mb-6">
          <div className="label-kicker">Get started</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1">Create your account</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Use your work email — we'll auto-organize teammates with the same domain into one workspace.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-xl border border-zinc-200 bg-white p-6">
          <div>
            <label className="text-xs font-medium text-zinc-600">Full name</label>
            <input
              data-testid="register-name-input"
              value={form.name}
              onChange={set("name")}
              required
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 bg-white"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Work email</label>
            <input
              data-testid="register-email-input"
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 bg-white"
              placeholder="you@company.com"
            />
            {hint ? (
              <div
                data-testid="register-domain-hint"
                className="mt-2 flex items-start gap-2 rounded-lg border border-indigo-100 bg-indigo-50 text-indigo-800 text-xs px-3 py-2"
              >
                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  We'll create or join a workspace for <span className="font-medium">{hint.domain}</span>. The first
                  teammate from a domain becomes the manager; everyone after joins as an employee.
                </span>
              </div>
            ) : null}
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">Password</label>
            <input
              data-testid="register-password-input"
              type="password"
              value={form.password}
              onChange={set("password")}
              minLength={6}
              required
              className="mt-1 w-full rounded-lg border border-zinc-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950/20 focus:border-zinc-950 bg-white"
              placeholder="At least 6 characters"
            />
          </div>

          {error ? (
            <div data-testid="register-error" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
              {error}
            </div>
          ) : null}

          <button
            data-testid="register-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-950 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
          >
            {busy ? "Creating…" : "Create account"} <ArrowRight className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2 text-[11px] text-zinc-400 pt-1">
            <Building2 className="w-3 h-3" />
            <span>Personal email providers (gmail, yahoo, outlook…) aren't supported.</span>
          </div>
        </form>

        <div className="mt-4 text-sm text-zinc-500 text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="go-login-link" className="text-zinc-900 font-medium hover:underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
